import { AxiosError } from 'axios';
import { HydraUIAPIError } from '@customerio/hydra-ui-api';
import { get, isString, upperFirst } from 'lodash-es';
import { getApiErrorMessage } from '@/utils/errors';
import { snackbarApi } from '@/services/snackbar';
import { extractErrorsIfBadRequest } from '@/utils/bad-request-errors';
import { sentryCaptureException } from '@/utils/sentry';

/**
 * A failed CDP request, read the same way from either transport: the typed
 * `APIClient` every `services/db/cdp-*` entity uses, and axios, whose errors
 * tests still build. `code` keeps axios's names so Sentry extras and fingerprints
 * stay comparable across the two.
 */
export type CdpRequestFailure = {
  /** Undefined when no response arrived: a lost connection or a cancel. */
  status: number | undefined;
  body: unknown;
  canceled: boolean;
  code: string | undefined;
};

/**
 * A typed CDP request that failed without an HTTP answer: a lost connection, or
 * a 2xx whose body never arrived whole or wasn't JSON. Browsers word these
 * differently, so the client wraps them rather than callers matching messages.
 * The message is axios's, which `notifyOnError` shows and analytics reads.
 */
export class CdpNetworkError extends Error {
  constructor(cause: unknown) {
    super('Network Error', { cause });
    this.name = 'CdpNetworkError';
  }
}

function isAbort(error: unknown): boolean {
  // fetch rejects a cancel with a DOMException, which isn't an Error subclass everywhere.
  return typeof error === 'object' && error != null && 'name' in error && error.name === 'AbortError';
}

/** Wraps a typed CDP call so anything but an HTTP failure or a cancel rejects as a CdpNetworkError. */
export async function withCdpTransportErrors<T>(request: Promise<T>): Promise<T> {
  try {
    return await request;
  } catch (error) {
    if (error instanceof HydraUIAPIError || isAbort(error)) {
      throw error;
    }
    throw new CdpNetworkError(error);
  }
}

/** Null for anything that isn't a request failure, such as a bug in the caller. */
export function cdpRequestFailure(error: unknown): CdpRequestFailure | null {
  if (error instanceof AxiosError) {
    return {
      status: error.response?.status,
      body: error.response?.data,
      canceled: error.code === AxiosError.ERR_CANCELED,
      code: error.code,
    };
  }
  if (error instanceof HydraUIAPIError) {
    return {
      status: error.status,
      body: error.body,
      canceled: false,
      code: error.status >= 500 ? AxiosError.ERR_BAD_RESPONSE : AxiosError.ERR_BAD_REQUEST,
    };
  }
  if (isAbort(error)) {
    return { status: undefined, body: undefined, canceled: true, code: AxiosError.ERR_CANCELED };
  }
  if (error instanceof CdpNetworkError) {
    return { status: undefined, body: undefined, canceled: false, code: AxiosError.ERR_NETWORK };
  }
  return null;
}

/** The messages a 400 carries, from either transport. Empty for anything else. */
export function cdpBadRequestMessages(error: unknown): unknown[] {
  if (error instanceof AxiosError) {
    return extractErrorsIfBadRequest(error);
  }
  if (!(error instanceof HydraUIAPIError) || error.status !== 400 || error.body == null) {
    return [];
  }
  const bodies: unknown[] = Array.isArray(error.body) ? error.body : [error.body];
  const isJson = error.headers?.get('content-type')?.includes('application/json') ?? false;
  return isJson ? bodies.map(badRequestMessage) : bodies;
}

/** Same reading as extractErrorsIfBadRequest: the item's message, or the item itself. */
function badRequestMessage(item: unknown): string {
  const message =
    typeof item === 'object' && item != null && 'message' in item && typeof item.message === 'string'
      ? upperFirst(item.message)
      : '';
  return message !== '' ? message : JSON.stringify(item);
}

/** The server's detail in a failed request's body: the same fields `getApiErrorBody` reads off an axios response. */
export function cdpServerMessage(error: unknown): string | undefined {
  const body = cdpRequestFailure(error)?.body;
  for (const path of ['errors[0].detail', 'message', 'error']) {
    const detail: unknown = get(body, path);
    if (typeof detail === 'string' && detail !== '') return detail;
  }
  return undefined;
}

/**
 * What to tell the user about a failed CDP request. Axios errors read as they
 * always have (`getApiErrorMessage`). A typed-client failure shows the server's
 * detail or the fallback, never HydraUIAPIError's own "Journeys UI API" message.
 */
export function cdpErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof AxiosError) return getApiErrorMessage(error, fallback);
  if (error instanceof CdpNetworkError) return error.message;
  return cdpServerMessage(error) ?? (cdpRequestFailure(error) == null ? getApiErrorMessage(error, fallback) : fallback);
}

/**
 * A failed request's message in the words axios used for CDP failures, which
 * snackbars and analytics have always shown. HydraUIAPIError's own message
 * names the Journeys UI API instead.
 */
export function cdpFailureMessage(error: Error): string {
  return error instanceof HydraUIAPIError ? `Request failed with status code ${error.status}` : error.message;
}

export function notifyOnError(error: unknown) {
  let message = '';

  const badRequest = cdpBadRequestMessages(error).join('\n');
  if (badRequest !== '') {
    message = badRequest;
  } else if (error instanceof Error) {
    message = cdpFailureMessage(error);
  } else {
    message = isString(error) ? error : JSON.stringify(error);
  }

  snackbarApi.create({ variant: 'critical', message });

  sentryCaptureException(error);
}
